import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthStore } from '../types/authStore.types';
import {
  INIT_IMP,
  checkIsCompanyScoped,
  buildImpersonationState,
  resolveEffectiveCompanyId,
  resolveEffectiveRoles,
  resolveActiveRole,
  resolveAvailableRoles,
  isAllowedRole,
} from '../utils/authStore.helpers';

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isInitialized: false,
      impersonation: INIT_IMP,
      activeRole: null,
      setUser: (user) => set({ user }),
      setToken: (token) => set({ token }),
      setLoading: (isLoading) => set({ isLoading }),
      setInitialized: (isInitialized) => set({ isInitialized }),
      logout: () => set({ user: null, token: null, impersonation: INIT_IMP, activeRole: null }),
      isAuthenticated: () => get().user !== null,
      hasRole: (role) => get().user?.roles.includes(role) ?? false,
      isSuperAdmin: () => get().hasRole('SUPER_ADMIN'),
      isCompanyAdmin: () => get().hasRole('ADMIN'),
      isAgent: () => get().hasRole('AGENT'),
      isManager: () => get().hasRole('MANAGER'),
      isCompanyScoped: () => checkIsCompanyScoped(get().user, get().impersonation, get().hasRole),
      startImpersonation: (session, context) => {
        const result = buildImpersonationState(get().user, session, context);
        if (result) set({ impersonation: result });
      },
      endImpersonation: () => set({ impersonation: INIT_IMP }),
      getEffectiveCompanyId: () => resolveEffectiveCompanyId(get().impersonation, get().user),
      getEffectiveRoles: () => resolveEffectiveRoles(get().impersonation, get().user),
      setActiveRole: (role) => {
        if (isAllowedRole(role, get().user, get().impersonation)) set({ activeRole: role });
      },
      getActiveRole: () => resolveActiveRole(get().activeRole, get().user, get().impersonation),
      getAvailableRoles: () => resolveAvailableRoles(get().user, get().impersonation),
    }),
    {
      name: 'auth-storage',
      partialize: (s) => ({
        user: s.user,
        token: s.token,
        impersonation: s.impersonation,
        activeRole: s.activeRole,
      }),
    }
  )
);
